import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ArrowLeftIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import toast from 'react-hot-toast';
import { supabase } from '../../config/supabase';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  color: ${props => props.$active ? props.theme.colors.accent : props.theme.colors.text.secondary};
  font-weight: ${props => props.$active ? '600' : '400'};
  border-bottom: 2px solid ${props => props.$active ? props.theme.colors.accent : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  &:hover {
    color: ${props => props.theme.colors.accent};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.accent};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
`;

const ContentArea = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PendingShows = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
`;

const ShowItem = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  position: relative;
`;

const ShowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ShowInfo = styled.div`
  flex: 1;
`;

const ShowTitle = styled.h3`
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: ${props => props.theme.typography.h4.fontWeight};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ShowDetails = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  line-height: 1.5;
`;

const ApprovalProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${props => props.theme.spacing.xs};
`;

const ProgressText = styled.div`
  font-size: ${props => props.theme.typography.small.fontSize};
  color: ${props => props.theme.colors.text.secondary};
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 8px;
  background: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.colors.accent};
  width: ${props => Math.max(0, Math.min(100, props.progress || 0))}%;
  transition: width 0.3s ease;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
`;

const ActionButton = styled.button`
  background: ${props => props.$approve ? props.theme.colors.gradient : 'transparent'};
  border: 1px solid ${props => props.$approve ? props.theme.colors.accent : props.theme.colors.error};
  color: ${props => props.$approve ? 'white' : props.theme.colors.error};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.caption.fontSize};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$approve ? props.theme.colors.gradientHover : props.theme.colors.error};
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.text.secondary};
`;

const Moderation = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingShows, setPendingShows] = useState([]);
  const [myPendingShows, setMyPendingShows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPending: 0,
    myShowsPending: 0,
    pointsEarned: 0
  });

  const getCategoryDisplay = (category) => {
    const categoryMap = {
      'spam': 'Spam or Fake Event',
      'inappropriate': 'Inappropriate Content',
      'wrong-info': 'Wrong Information',
      'scam': 'Scam or Fraud',
      'harassment': 'Harassment',
      'other': 'Other'
    };
    return categoryMap[category] || 'General';
  };

  const shouldHideReporterName = (category) => {
    // Hide reporter identity for sensitive categories to protect from retaliation
    const sensitiveCategories = ['inappropriate', 'harassment', 'other'];
    return sensitiveCategories.includes(category);
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const loadModerationData = async () => {
      try {
        // Load moderation queue
        const allPendingData = await apiService.getModerationQueue();
        
        // Filter out shows user has already moderated AND user's own shows if they're a fan
        const filteredPending = allPendingData.filter(show => {
          // Hide shows user has already approved or rejected
          const userAlreadyModerated = show.approvedBy?.includes(currentUser.id) || 
                                     show.rejectedBy?.includes(currentUser.id);
          
          // Hide user's own shows if they're a fan (not band/venue)
          const isOwnShow = show.poster_id === currentUser.id;
          const isFan = currentUser.user_type === 'fan';
          
          return !userAlreadyModerated && !(isOwnShow && isFan);
        });
        setPendingShows(filteredPending);

        // Load user's own pending shows
        const myShowsData = await apiService.getUserShowsForModeration(currentUser.id);
        const myPendingOnly = myShowsData.filter(show => show.approval_status === 'pending');
        setMyPendingShows(myPendingOnly);

        // Calculate stats
        setStats(prev => ({
          ...prev,
          totalPending: filteredPending.length,
          myShowsPending: myPendingOnly.length
        }));
      } catch (error) {
        console.error('Error loading moderation data:', error);
        toast.error('Failed to load moderation data');
      } finally {
        setIsLoading(false);
      }
    };

    loadModerationData();

    // Set up real-time subscription for moderation queue updates
    const subscription = supabase
      .channel(`moderation_queue_${Date.now()}`)
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shows',
          filter: 'approval_status=in.(pending,reported)'
        },
        (payload) => {
          console.log('🔄 Moderation queue updated:', payload);
          // Reload moderation data when shows are reported or status changes
          loadModerationData();
        }
      )
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'show_reports'
        },
        (payload) => {
          console.log('🚩 Report added to moderation queue:', payload);
          // Reload moderation data when new reports are added
          loadModerationData();
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'shows',
          filter: `poster_id=eq.${currentUser.id}`
        },
        (payload) => {
          console.log('📢 Your show status updated:', payload);
          // Check if show was approved
          if (payload.new.approval_status === 'approved' && payload.old.approval_status === 'pending') {
            toast.success(`🎉 Your show "${payload.new.band_name}" has been approved and is now live!`, {
              duration: 8000,
              icon: '🔔',
              style: {
                background: '#10B981',
                color: 'white',
              },
            });
            // Reload data to update counts
            loadModerationData();
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser, navigate]);

  const handleApprove = async (showId) => {
    try {
      const result = await apiService.approveShow(showId);
      
      if (result.success) {
        toast.success(result.message);
        setPendingShows(prevShows => prevShows.filter(show => show.id !== showId));
        setStats(prev => ({
          ...prev,
          totalPending: prev.totalPending - 1,
          pointsEarned: prev.pointsEarned + (result.pointsAdded || 0)
        }));
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error approving show:', error);
      toast.error('An error occurred while approving the show.');
    }
  };

  const handleReject = async (showId) => {
    try {
      const result = await apiService.rejectShow(showId);
      
      if (result.success) {
        toast.success(result.message);
        setPendingShows(prevShows => prevShows.filter(show => show.id !== showId));
        setStats(prev => ({
          ...prev,
          totalPending: prev.totalPending - 1,
          pointsEarned: prev.pointsEarned + (result.pointsAdded || 0)
        }));
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error rejecting show:', error);
      toast.error('An error occurred while rejecting the show.');
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <ClockIcon style={{ width: 48, height: 48, margin: '0 auto 1rem' }} />
          <div>Loading moderation dashboard...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#8B5CF6', fontSize: '1.2em', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, marginBottom: '8px', cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: 24, height: 24 }} />
          Back
        </button>
        <Title>Moderation</Title>
      </Header>

      <Stats>
        <StatCard>
          <StatValue>{stats.totalPending}</StatValue>
          <StatLabel>Available to Moderate</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.myShowsPending}</StatValue>
          <StatLabel>My Shows Pending</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.pointsEarned}</StatValue>
          <StatLabel>Points Earned Today</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{currentUser?.scenePoints || 0}</StatValue>
          <StatLabel>Total Scene Points</StatLabel>
        </StatCard>
      </Stats>

      <TabContainer>
        <Tab 
          $active={activeTab === 'pending'} 
          onClick={() => setActiveTab('pending')}
        >
          <ClockIcon />
          Pending ({stats.totalPending})
        </Tab>
        <Tab 
          $active={activeTab === 'mypending'} 
          onClick={() => setActiveTab('mypending')}
        >
          <UserIcon />
          My Pending Shows ({stats.myShowsPending})
        </Tab>
      </TabContainer>

      <ContentArea>
        {activeTab === 'pending' && (
          <>
            {pendingShows.length === 0 ? (
              <EmptyState>
                <CheckIcon style={{ width: 64, height: 64, margin: '0 auto 1rem', opacity: 0.5 }} />
                <h3>All caught up!</h3>
                <p>No shows available for moderation right now.</p>
              </EmptyState>
            ) : (
              <PendingShows>
                {pendingShows.map((show) => (
                  <ShowItem
                    key={show.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShowHeader>
                      <ShowInfo>
                        <ShowTitle>{show.band_name}</ShowTitle>
                        <ShowDetails>
                          📍 {show.venue_name}<br/>
                          📅 {new Date(show.show_date).toLocaleDateString()} at {show.show_time}<br/>
                          🎵 {Array.isArray(show.genre) ? show.genre.join(', ') : show.genre}<br/>
                          👤 Posted by <strong>{show.poster?.username}</strong> ({show.poster?.user_type})
                        </ShowDetails>
                      </ShowInfo>
                      <ApprovalProgress>
                        <ProgressText>
                          {show.show_approvals?.length || 0}/{show.required_approvals || 3} approvals
                        </ProgressText>
                        <ProgressBar>
                          <ProgressFill 
                            progress={((show.show_approvals?.length || 0) / (show.required_approvals || 3)) * 100} 
                          />
                        </ProgressBar>
                      </ApprovalProgress>
                    </ShowHeader>

                    <ShowDetails style={{ marginBottom: '1rem' }}>
                      <strong>Description:</strong> {show.description}
                    </ShowDetails>

                    {show.current_reports?.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '1rem', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px' }}>
                        <strong style={{ color: '#DC2626', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          🚩 {show.current_reports.length} Total Report{show.current_reports.length !== 1 ? 's' : ''}
                        </strong>
                        {show.current_reports.map((report, index) => (
                          <div key={index} style={{ marginBottom: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong style={{ color: '#374151' }}>
                                  {shouldHideReporterName(report.category) ? 'Anonymous Reporter' : 'Reporter'}
                                </strong> 
                                {report.report_weight > 1 && <span style={{ color: '#DC2626', fontWeight: 'bold' }}> (counts as {report.report_weight})</span>}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                                {new Date(report.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#DC2626', fontWeight: '600', marginBottom: '0.25rem' }}>
                              Reason: {getCategoryDisplay(report.category)}
                            </div>
                            {report.message && (
                              <div style={{ fontSize: '0.875rem', color: '#374151', fontStyle: 'italic', marginTop: '0.5rem', padding: '0.5rem', background: '#F9FAFB', borderRadius: '4px' }}>
                                "{report.message}"
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <ActionButtons>
                      <ActionButton
                        $approve={true}
                        onClick={() => handleApprove(show.id)}
                        disabled={show.approvedBy && show.approvedBy.includes(currentUser.id)}
                      >
                        <CheckIcon />
                        {show.approvedBy && show.approvedBy.includes(currentUser.id) ? 
                          'Already Approved' : 
                          `Approve (+${currentUser?.scenePoints >= 500 ? 3 : currentUser?.scenePoints >= 100 ? 2 : 1} points)`
                        }
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleReject(show.id)}
                        disabled={show.rejectedBy && show.rejectedBy.includes(currentUser.id)}
                      >
                        <XMarkIcon />
                        {show.rejectedBy && show.rejectedBy.includes(currentUser.id) ? 
                          'Already Rejected' : 
                          `Reject (+1 point)`
                        }
                      </ActionButton>
                    </ActionButtons>
                  </ShowItem>
                ))}
              </PendingShows>
            )}
          </>
        )}

        {activeTab === 'mypending' && (
          <>
            {myPendingShows.length === 0 ? (
              <EmptyState>
                <UserIcon style={{ width: 64, height: 64, margin: '0 auto 1rem', opacity: 0.5 }} />
                <h3>No pending shows</h3>
                <p>You don't have any shows awaiting approval right now.</p>
              </EmptyState>
            ) : (
              <PendingShows>
                {myPendingShows.map((show) => (
                  <ShowItem
                    key={show.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShowHeader>
                      <ShowInfo>
                        <ShowTitle>{show.band_name}</ShowTitle>
                        <ShowDetails>
                          📍 {show.venue_name}<br/>
                          📅 {new Date(show.show_date).toLocaleDateString()} at {show.show_time}<br/>
                          🎵 {Array.isArray(show.genre) ? show.genre.join(', ') : show.genre}<br/>
                          📊 Status: <strong style={{ color: show.approval_status === 'approved' ? '#10B981' : show.approval_status === 'rejected' ? '#EF4444' : '#F59E0B' }}>
                            {show.approval_status?.charAt(0).toUpperCase() + show.approval_status?.slice(1)}
                          </strong>
                        </ShowDetails>
                      </ShowInfo>
                      <ApprovalProgress>
                        <ProgressText>
                          {show.approvalsReceived || 0}/{show.approvalsNeeded || 3} approvals
                        </ProgressText>
                        <ProgressBar>
                          <ProgressFill 
                            progress={((show.approvalsReceived || 0) / (show.approvalsNeeded || 3)) * 100} 
                          />
                        </ProgressBar>
                      </ApprovalProgress>
                    </ShowHeader>

                    <ShowDetails style={{ marginBottom: '1rem' }}>
                      <strong>Description:</strong> {show.description}
                    </ShowDetails>

                    {show.approval_status === 'pending' && (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#FEF3C7', 
                        border: '1px solid #F59E0B', 
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ color: '#92400E', fontWeight: '600', marginBottom: '0.5rem' }}>
                          ⏳ Awaiting Community Approval
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#92400E' }}>
                          Your show needs {(show.approvalsNeeded || 3) - (show.approvalsReceived || 0)} more approval{(show.approvalsNeeded || 3) - (show.approvalsReceived || 0) !== 1 ? 's' : ''} from the community before it goes live.
                        </div>
                      </div>
                    )}

                    {show.approval_status === 'approved' && (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#D1FAE5', 
                        border: '1px solid #10B981', 
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ color: '#065F46', fontWeight: '600' }}>
                          ✅ Show Approved & Live!
                        </div>
                      </div>
                    )}

                    {show.approval_status === 'rejected' && (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#FEE2E2', 
                        border: '1px solid #EF4444', 
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ color: '#991B1B', fontWeight: '600' }}>
                          ❌ Show Rejected
                        </div>
                      </div>
                    )}
                  </ShowItem>
                ))}
              </PendingShows>
            )}
          </>
        )}


      </ContentArea>
    </Container>
  );
};

export default Moderation; 